import React, { useState, useCallback, useEffect, useMemo } from 'react';
import type { StructuredPatchHunk } from 'diff';
import { useTheme } from 'next-themes';
import { cn } from '@reflex/ui/lib/utils';
import {
  Check,
  Copy,
  CheckCircle2,
  XCircle,
  FileText,
  Pencil,
  Trash2,
  Search,
  SquareTerminal,
  Globe,
  Brain,
  Wrench,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { Spinner } from '@reflex/ui/components/shared/Spinner';
import { Button } from '@reflex/ui/components/ui/button';
import { ScannedPath } from '@reflex/ui/components/content/ScannedPath';
import { Tooltip, TooltipContent, TooltipTrigger } from '@reflex/ui/components/ui/tooltip';
import { AnsiText } from '@reflex/ui/components/content/AnsiText';
import {
  highlightCode,
  type HighlightedLines,
  type ThemedToken,
} from '@reflex/ui/lib/syntax-highlight';
import type { ToolKind, ToolCallStatus, ContentItem, DiffContent } from '../types.js';
import {
  splitTokensAtRanges,
  getDiffHunks,
  getDiffLineCounts,
  getDiffSizeIssue,
  getDiffWordRanges,
  MAX_DIFF_EDIT_DISTANCE,
  type DiffSizeIssue,
  type DiffLineCounts,
  type CharRange,
} from './diff-utils.js';

export function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    },
    [text],
  );
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          onClick={handleCopy}
          className={cn(
            'h-auto rounded px-1.5 py-0.5 font-normal text-muted-foreground',
            'hover:bg-muted/50 hover:text-foreground dark:hover:bg-muted/50',
            className,
          )}
        >
          {copied ? (
            <>
              <Check className="size-3 text-status-success" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy className="size-3" />
              <span>Copy</span>
            </>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>Copy</TooltipContent>
    </Tooltip>
  );
}

export const TOOL_KIND_META: Record<ToolKind, { icon: React.ElementType; label: string }> = {
  read: { icon: FileText, label: 'Read' },
  edit: { icon: Pencil, label: 'Edit' },
  delete: { icon: Trash2, label: 'Delete' },
  move: { icon: ArrowRight, label: 'Move' },
  search: { icon: Search, label: 'Search' },
  execute: { icon: SquareTerminal, label: 'Run' },
  think: { icon: Brain, label: 'Think' },
  fetch: { icon: Globe, label: 'Fetch' },
  switch_mode: { icon: RefreshCw, label: 'Mode' },
  other: { icon: Wrench, label: 'Tool' },
};

export function toolKindMeta(kind: ToolKind) {
  return TOOL_KIND_META[kind] ?? TOOL_KIND_META.other;
}

export function StatusIndicator({ status }: { status: ToolCallStatus }) {
  switch (status) {
    case 'pending':
    case 'in_progress':
      return <Spinner size="xs" className="text-status-info" />;
    case 'completed':
      return <CheckCircle2 className="h-3 w-3 shrink-0 text-status-success" />;
    case 'failed':
      return <XCircle className="h-3 w-3 shrink-0 text-status-error" />;
  }
}

function renderTokens(tokens: ThemedToken[] | undefined, fallback: string): React.ReactNode {
  if (!tokens) return fallback;
  return tokens.map((token, i) => (
    <span key={i} style={token.color ? { color: token.color } : undefined}>
      {token.content}
    </span>
  ));
}

/**
 * Inline, lazily syntax-highlighted code for the compact tool-call header row
 * and its overflow tooltip. Highlighting loads Shiki in a separate chunk on
 * first use (see {@link highlightCode}); until it lands — and whenever the
 * language is unknown or highlighting is disabled (Storybook play tests) — the
 * plain `code` renders instead, so the text stays selectable and assertable
 * throughout. Token colors come straight from the active theme; the parent
 * element owns the font and any truncation/direction styling.
 */
export function InlineHighlightedCode({ code, lang }: { code: string; lang: string }) {
  const { resolvedTheme } = useTheme();
  const theme = resolvedTheme === 'light' ? 'light' : 'dark';
  const [lines, setLines] = useState<HighlightedLines | null>(null);

  useEffect(() => {
    let cancelled = false;
    void highlightCode(code, '', theme, lang).then((hl) => {
      if (!cancelled) setLines(hl);
    });
    return () => {
      cancelled = true;
    };
  }, [code, lang, theme]);

  if (!lines) return <>{code}</>;
  return (
    <>
      {lines.map((tokens, li) => (
        <React.Fragment key={li}>
          {li > 0 ? '\n' : null}
          {renderTokens(tokens, '')}
        </React.Fragment>
      ))}
    </>
  );
}

const LINE_BG: Record<string, string> = {
  '+': 'rgba(34,197,94,0.15)',
  '-': 'rgba(239,68,68,0.15)',
};

/** Bolder than {@link LINE_BG}, layered on top of it for the spans a word-diff identifies as actually changed. */
const WORD_HIGHLIGHT_BG: Record<string, string> = {
  '+': 'rgba(34,197,94,0.4)',
  '-': 'rgba(239,68,68,0.4)',
};

/** Compact `+N -M` line-count badge, shown on a diff's header and on the tool-call row itself. */
export function DiffLineCountBadge({ counts }: { counts: DiffLineCounts | null }) {
  if (!counts || (counts.added === 0 && counts.removed === 0)) return null;
  return (
    <span className="flex shrink-0 items-center gap-1.5 font-mono text-3xs tabular-nums">
      {counts.added > 0 && <span className="text-status-success">+{counts.added}</span>}
      {counts.removed > 0 && <span className="text-status-error">-{counts.removed}</span>}
    </span>
  );
}

interface HunkLineData {
  line: string;
  prefix: string;
  tokens: ThemedToken[] | undefined;
  oldLineNo: number | null;
  newLineNo: number | null;
  /** Word-diff change ranges for this line, when it's paired with an adjacent replaced line. */
  wordRanges: CharRange[] | undefined;
}

function buildHunkLines(
  hunks: StructuredPatchHunk[],
  /** Word-diff ranges per hunk, indexed by position within that hunk's lines. */
  wordRangesByHunk: Array<Array<CharRange[] | undefined>>,
  highlighted: { old: HighlightedLines | null; new: HighlightedLines | null },
): { hunk: StructuredPatchHunk; lines: HunkLineData[] }[] {
  return hunks.map((hunk, hunkIdx) => {
    let oldLineNo = hunk.oldStart;
    let newLineNo = hunk.newStart;
    let oldIdx = hunk.oldStart - 1;
    let newIdx = hunk.newStart - 1;

    const wordRanges = wordRangesByHunk[hunkIdx] ?? [];

    const lines: HunkLineData[] = hunk.lines.map((line: string, i: number) => {
      const prefix = line[0];
      let tokens: ThemedToken[] | undefined;
      let lineData: HunkLineData;
      if (prefix === '-') {
        tokens = highlighted.old?.[oldIdx];
        lineData = { line, prefix, tokens, oldLineNo, newLineNo: null, wordRanges: wordRanges[i] };
        oldIdx++;
        oldLineNo++;
      } else if (prefix === '+') {
        tokens = highlighted.new?.[newIdx];
        lineData = { line, prefix, tokens, oldLineNo: null, newLineNo, wordRanges: wordRanges[i] };
        newIdx++;
        newLineNo++;
      } else if (prefix === '\\') {
        // "\ No newline at end of file" — not a real line, so it doesn't
        // consume an old/new index or token, and it isn't pairable.
        lineData = {
          line,
          prefix,
          tokens: undefined,
          oldLineNo: null,
          newLineNo: null,
          wordRanges: undefined,
        };
      } else {
        tokens = highlighted.new?.[newIdx];
        lineData = { line, prefix, tokens, oldLineNo, newLineNo, wordRanges: undefined };
        oldIdx++;
        newIdx++;
        oldLineNo++;
        newLineNo++;
      }
      return lineData;
    });

    return { hunk, lines };
  });
}

/**
 * Render a changed line with its word-diff ranges layered over the Shiki
 * tokens: a bolder background on the spans that actually changed, on top of
 * the existing whole-line green/red tint. Falls back to the plain
 * {@link renderTokens} rendering when there's nothing to highlight (context
 * lines, or a line with no paired counterpart to diff against).
 */
function renderDiffLine(
  tokens: ThemedToken[] | undefined,
  lineText: string,
  wordRanges: CharRange[] | undefined,
  prefix: string,
): React.ReactNode {
  if (!wordRanges || wordRanges.length === 0) return renderTokens(tokens, lineText);
  const segments = splitTokensAtRanges(tokens, lineText, wordRanges);
  return segments.map((seg, i) => (
    <span
      key={i}
      style={{
        color: seg.color,
        backgroundColor: seg.highlighted ? WORD_HIGHLIGHT_BG[prefix] : undefined,
      }}
    >
      {seg.text}
    </span>
  ));
}

const IMAGE_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'bmp',
  'ico',
  'avif',
]);

/** Extension check mirroring the filesystem plugin's image detection. */
export function isImagePath(path: string): boolean {
  const name = path.split('/').pop() ?? path;
  const dot = name.lastIndexOf('.');
  return dot >= 0 && IMAGE_EXTENSIONS.has(name.slice(dot + 1).toLowerCase());
}

/**
 * The file a diff or read card is about, as its basename, opening the preview.
 * The card's path can be relative — a shell write names the file exactly as the
 * command did — which `ScannedPath` resolves against the session's working
 * directory.
 */
function CardPathLabel({ path }: { path: string }) {
  return <ScannedPath path={path} label={path.split('/').pop() ?? path} />;
}

/**
 * Render a tool-call file diff. A text diff is meaningless for an image, and
 * its old bytes are already gone from the devbox, so image paths show the
 * current file instead: `ScannedPath` resolves the filesystem plugin's inline
 * preview (a lazy, base64-encoded thumbnail that expands to the full popover).
 */
export function UnifiedDiffView({ diff }: { diff: DiffContent }) {
  if (isImagePath(diff.path)) {
    return <ImageDiffView diff={diff} />;
  }
  if (diff.originalUnavailable) {
    return <WrittenFileView diff={diff} />;
  }
  return <TextDiffView diff={diff} />;
}

/** The note on a {@link WrittenFileView}, explaining why it isn't a diff. */
const ORIGINAL_UNAVAILABLE_NOTE =
  'This file already existed. Its previous contents were not captured, so this shows the file as written, not what changed.';

/**
 * A write over a file whose previous contents nothing in the tool result
 * carried. Diffing against an empty file would render every line green and
 * badge the row with `+N` for a file that may have changed by one character, so
 * the card shows the contents as written, with no diff markup and no line
 * counts, and says why.
 */
function WrittenFileView({ diff }: { diff: DiffContent }) {
  const issue = getDiffSizeIssue(diff);
  // Same size guards as a diff, but the wording can't talk about a diff or a
  // change: all this card ever had was the file as written.
  if (issue) return <FileCardMessage path={diff.path} message={writtenFileMessage(issue)} />;
  return (
    <ReadOutputView
      path={diff.path}
      lines={numberFileLines(diff.newText)}
      note={ORIGINAL_UNAVAILABLE_NOTE}
    />
  );
}

function ImageDiffView({ diff }: { diff: DiffContent }) {
  const basename = diff.path.split('/').pop() ?? diff.path;
  return (
    <div className="rounded-md border border-border/50 bg-muted/20 overflow-hidden text-xs font-mono">
      <div className="flex items-center gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
        <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="min-w-0 flex-1 truncate text-muted-foreground">{basename}</span>
      </div>
      <div className="flex justify-center bg-background/40 p-3">
        <CardPathLabel path={diff.path} />
      </div>
    </div>
  );
}

/** The message for each reason a file shown as written falls back to a summary. */
function writtenFileMessage(issue: DiffSizeIssue): string {
  switch (issue.kind) {
    case 'binary':
      return 'Binary file written. Preview not shown.';
    case 'oversized-line':
      return `File too large to preview: ${issue.charCount.toLocaleString()} characters on one line.`;
    case 'too-large':
      return `File too large to preview: ${issue.charCount.toLocaleString()} characters.`;
    case 'too-many-changes':
      return 'File too large to preview.';
  }
}

/** The message for each reason a diff falls back to a summary. */
function fallbackMessage(issue: DiffSizeIssue): string {
  switch (issue.kind) {
    case 'binary':
      return 'Binary file changed — preview not shown.';
    case 'oversized-line':
      return `Large single-line change — ${issue.charCount.toLocaleString()} characters on one line.`;
    case 'too-large':
      return `Diff too large to render in full — ${issue.charCount.toLocaleString()} characters.`;
    case 'too-many-changes':
      return `Diff too large to render in full — over ${MAX_DIFF_EDIT_DISTANCE.toLocaleString()} changed lines.`;
  }
}

/**
 * Compact fallback for a diff too large or too pathological to render in full
 * (binary content, an oversized file, a single line of huge length, or a change
 * with more edits than the diff ceiling allows) — the word-diff + Shiki
 * treatment would either be meaningless (binary) or make the view unusably
 * slow/huge. Keeps the same path header as the real diff so the row still
 * communicates what happened. No line-count badge: counting these changes
 * exactly is the expensive work the fallback exists to skip (see the
 * `fallbackIssue` short-circuit in {@link TextDiffView}).
 */
function DiffFallbackSummary({ diff, issue }: { diff: DiffContent; issue: DiffSizeIssue }) {
  return <FileCardMessage path={diff.path} message={fallbackMessage(issue)} />;
}

/** A file card whose body is a single line of prose instead of file contents. */
function FileCardMessage({ path, message }: { path: string; message: string }) {
  return (
    <div className="rounded-md border border-border/50 bg-muted/20 overflow-hidden text-xs font-mono">
      <div className="flex items-center gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
        <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1 truncate text-muted-foreground">
          <CardPathLabel path={path} />
        </div>
      </div>
      <p className="p-3 text-muted-foreground/70">{message}</p>
    </div>
  );
}

/** Empty hunk list for the fallback path, hoisted so it doesn't change identity between renders. */
const NO_HUNKS: StructuredPatchHunk[] = [];
const NO_WORD_RANGES: Array<Array<CharRange[] | undefined>> = [];

function TextDiffView({ diff }: { diff: DiffContent }) {
  const oldStr = diff.oldText ?? '';
  const newStr = diff.newText;
  // Why the diff doesn't render, if it doesn't: pathological content caught by
  // the cheap size guards, or a change whose edit script blew past
  // `MAX_DIFF_EDIT_DISTANCE`. Both answers are memoized per diff content item
  // inside `diff-utils`, so this costs one lookup on a re-render and shares its
  // work with the collapsed row's line-count badge; the `useMemo` keeps the
  // returned object's identity stable for the effect below.
  const fallbackIssue = useMemo((): DiffSizeIssue | null => {
    const sizeIssue = getDiffSizeIssue(diff);
    if (sizeIssue) return sizeIssue;
    return getDiffHunks(diff) === null ? { kind: 'too-many-changes' } : null;
  }, [diff]);

  const { resolvedTheme } = useTheme();
  const highlightTheme = resolvedTheme === 'light' ? 'light' : 'dark';

  const [highlighted, setHighlighted] = useState<{
    old: HighlightedLines | null;
    new: HighlightedLines | null;
  }>({ old: null, new: null });

  useEffect(() => {
    // Skip Shiki entirely for a diff that won't render — there's no point
    // tokenizing text only the fallback summary will stand in for.
    if (fallbackIssue) return;
    let cancelled = false;
    void Promise.all([
      oldStr ? highlightCode(oldStr, diff.path, highlightTheme) : Promise.resolve(null),
      highlightCode(newStr, diff.path, highlightTheme),
    ]).then(([oldHL, newHL]) => {
      if (!cancelled) setHighlighted({ old: oldHL, new: newHL });
    });
    return () => {
      cancelled = true;
    };
  }, [oldStr, newStr, diff.path, highlightTheme, fallbackIssue]);

  // These all come out of the shared per-diff memo: `getDiffHunks` already ran
  // in `fallbackIssue` above, and the counts and word ranges are derived from
  // those same hunks — so a re-render for arriving syntax tokens re-lays out
  // the lines without re-diffing anything.
  const hunks = fallbackIssue ? NO_HUNKS : (getDiffHunks(diff) ?? NO_HUNKS);
  const counts = fallbackIssue ? null : getDiffLineCounts(diff);
  const wordRanges = fallbackIssue ? NO_WORD_RANGES : getDiffWordRanges(diff);
  const hunkLines = useMemo(
    () => buildHunkLines(hunks, wordRanges, highlighted),
    [hunks, wordRanges, highlighted],
  );

  if (fallbackIssue) {
    return <DiffFallbackSummary diff={diff} issue={fallbackIssue} />;
  }

  return (
    <div className="rounded-md border border-border/50 bg-muted/20 overflow-hidden text-xs font-mono">
      <div className="flex items-center gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
        <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1 truncate text-muted-foreground">
          <CardPathLabel path={diff.path} />
        </div>
        <DiffLineCountBadge counts={counts} />
        <CopyButton text={newStr} />
      </div>
      <div className="max-h-96 overflow-auto">
        <div className="min-w-full w-fit">
          {hunkLines.map(({ hunk, lines }, hi) => (
            <div key={hi}>
              <div className="sticky left-0 bg-muted/40 py-0.5 pl-[4.25rem] text-xs text-muted-foreground/60 select-none">
                @@ -{hunk.oldStart},{hunk.oldLines} +{hunk.newStart},{hunk.newLines} @@
              </div>
              {lines.map(
                ({ line, prefix, tokens, oldLineNo, newLineNo, wordRanges: lineRanges }, li) => (
                  <div
                    key={li}
                    className="flex leading-5"
                    style={LINE_BG[prefix] ? { backgroundColor: LINE_BG[prefix] } : undefined}
                  >
                    <div
                      className="sticky left-0 z-10 flex shrink-0 select-none bg-background"
                      style={
                        LINE_BG[prefix]
                          ? {
                              background: `linear-gradient(${LINE_BG[prefix]},${LINE_BG[prefix]}),var(--color-background)`,
                            }
                          : undefined
                      }
                    >
                      <span className="flex border-r border-border/30 text-xs tabular-nums text-muted-foreground/40">
                        <span className="w-8 pr-2 text-right">{oldLineNo ?? ''}</span>
                        <span className="w-8 pr-2 text-right">{newLineNo ?? ''}</span>
                      </span>
                      <span
                        className={cn(
                          'w-5 text-center',
                          prefix === '+' && 'text-status-success',
                          prefix === '-' && 'text-status-error',
                          prefix === ' ' && 'text-muted-foreground/30',
                        )}
                      >
                        {prefix === ' ' ? '' : prefix}
                      </span>
                    </div>
                    <span className="whitespace-pre pr-4">
                      {renderDiffLine(tokens, line.slice(1), lineRanges, prefix)}
                    </span>
                  </div>
                ),
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export interface ReadOutputLine {
  lineNo: number;
  content: string;
}

const READ_OUTPUT_LINE = /^\s*(\d+)\t(.*)$/;

/**
 * Parse a Read tool result rendered in `cat -n` style (`   12\tcontent`).
 * Returns null unless every line matches, so anything unexpected (error text,
 * appended reminders, truncation notices) keeps the generic output panel.
 */
export function parseReadOutput(text: string): ReadOutputLine[] | null {
  const lines = text.replace(/\n$/, '').split('\n');
  const parsed: ReadOutputLine[] = [];
  for (const line of lines) {
    const m = READ_OUTPUT_LINE.exec(line);
    if (!m) return null;
    parsed.push({ lineNo: Number(m[1]), content: m[2] });
  }
  return parsed.length > 0 ? parsed : null;
}

/** Number every line of a file's contents, dropping the empty line a trailing newline leaves behind. */
export function numberFileLines(text: string): ReadOutputLine[] {
  const lines = text.replace(/\n$/, '').split('\n');
  return lines.map((content, i) => ({ lineNo: i + 1, content }));
}

/**
 * Styled Read tool output: the same file-card treatment as {@link TextDiffView}
 * (path header, line-number gutter, syntax highlighting) minus the +/- column.
 * `note` states why the card shows contents instead of a diff, when it does.
 */
export function ReadOutputView({
  path,
  lines,
  note,
}: {
  path: string;
  lines: ReadOutputLine[];
  note?: string;
}) {
  const { resolvedTheme } = useTheme();
  const highlightTheme = resolvedTheme === 'light' ? 'light' : 'dark';
  const content = lines.map((l) => l.content).join('\n');

  const [highlighted, setHighlighted] = useState<HighlightedLines | null>(null);
  useEffect(() => {
    let cancelled = false;
    void highlightCode(content, path, highlightTheme).then((hl) => {
      if (!cancelled) setHighlighted(hl);
    });
    return () => {
      cancelled = true;
    };
  }, [content, path, highlightTheme]);

  const gutterCh = String(lines[lines.length - 1]?.lineNo ?? 1).length + 2;

  return (
    <div
      data-testid="read-output-view"
      className="rounded-md border border-border/50 bg-muted/20 overflow-hidden text-xs font-mono"
    >
      <div className="flex items-center gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
        <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1 truncate text-muted-foreground">
          <CardPathLabel path={path} />
        </div>
        <CopyButton text={content} />
      </div>
      {note && (
        <p
          data-testid="file-card-note"
          className="border-b border-border/50 px-3 py-1.5 text-muted-foreground/70"
        >
          {note}
        </p>
      )}
      <div className="max-h-96 overflow-auto">
        <div className="min-w-full w-fit">
          {lines.map(({ lineNo, content: lineContent }, i) => (
            <div key={i} className="flex leading-5">
              <span
                className="sticky left-0 z-10 shrink-0 select-none border-r border-border/30 bg-background pr-2 text-right tabular-nums text-muted-foreground/40"
                style={{ minWidth: `${gutterCh}ch` }}
              >
                {lineNo}
              </span>
              <span className="whitespace-pre px-3">
                {renderTokens(highlighted?.[i], lineContent)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function ToolCallContentView({
  content,
  rawOutput,
}: {
  content: ContentItem[];
  rawOutput?: unknown;
}) {
  if (content.length === 0 && rawOutput != null) {
    return <RawOutputView rawOutput={rawOutput} />;
  }

  return (
    <>
      {content.map((item, i) => {
        if (item.type === 'diff' && item.diff) {
          return <UnifiedDiffView key={i} diff={item.diff} />;
        }
        if (item.type === 'terminal' && item.terminal) {
          return (
            <div key={i} className="rounded-md border border-border/50 bg-muted/20 overflow-hidden">
              <div className="flex items-center gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
                <SquareTerminal className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate text-xs text-muted-foreground">
                  Terminal: {item.terminal.terminalId}
                </span>
              </div>
              <pre className="max-h-48 overflow-auto p-3 text-xs font-mono whitespace-pre-wrap leading-relaxed text-foreground/70">
                (no output captured)
              </pre>
            </div>
          );
        }
        if (item.text) {
          return (
            <div key={i} className="rounded-md border border-border/50 bg-muted/20 overflow-hidden">
              <div className="flex items-center gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
                <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate text-xs text-muted-foreground">Output</span>
                <CopyButton text={item.text} />
              </div>
              <pre className="max-h-48 overflow-auto p-3 text-xs font-mono whitespace-pre-wrap leading-relaxed text-foreground/70">
                <AnsiText text={item.text} />
              </pre>
            </div>
          );
        }
        return null;
      })}
    </>
  );
}

export function RawOutputView({ rawOutput }: { rawOutput: unknown }) {
  const text = typeof rawOutput === 'string' ? rawOutput : JSON.stringify(rawOutput, null, 2);
  return (
    <div className="rounded-md border border-border/50 bg-muted/20 overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border/50 bg-muted/30 px-3 py-1.5">
        <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="flex-1 truncate text-xs text-muted-foreground">Output</span>
        <CopyButton text={text} />
      </div>
      <pre className="max-h-48 overflow-auto p-3 text-xs font-mono whitespace-pre-wrap leading-relaxed text-foreground/70">
        <AnsiText text={text} />
      </pre>
    </div>
  );
}

export function ToolCallErrorView({ rawOutput, text }: { rawOutput?: unknown; text?: string }) {
  let msg: string | undefined = text;
  if (msg == null) {
    if (rawOutput == null) return null;
    if (typeof rawOutput === 'string') {
      msg = rawOutput;
    } else if (typeof rawOutput === 'object') {
      const obj = rawOutput as Record<string, unknown>;
      msg = (obj.message as string) ?? (obj.error as string) ?? JSON.stringify(rawOutput, null, 2);
    } else {
      msg = String(rawOutput);
    }
  }
  return (
    <div className="rounded-md border border-status-error/30 bg-status-error/5 overflow-hidden">
      <div className="flex items-center gap-2 border-b border-status-error/20 bg-status-error/5 px-3 py-1.5">
        <XCircle className="h-3 w-3 shrink-0 text-status-error" />
        <span className="flex-1 truncate text-xs text-status-error">Error</span>
        <CopyButton text={msg} />
      </div>
      <pre className="max-h-48 overflow-auto p-3 font-mono text-xs whitespace-pre-wrap leading-relaxed text-status-error">
        <AnsiText text={msg} />
      </pre>
    </div>
  );
}

export function CapabilityBadges({ caps }: { caps: Record<string, unknown> }) {
  const badges = flattenCapabilities(caps);
  if (badges.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {badges.map((b, i) => (
        <span
          key={i}
          className={cn(
            'inline-flex items-center rounded-md border px-1.5 py-0.5 font-mono text-xs',
            b.enabled
              ? 'border-status-success/30 bg-status-success/10 text-status-success'
              : 'border-border/60 bg-muted/40 text-muted-foreground/50 line-through',
          )}
        >
          {b.label}
        </span>
      ))}
    </div>
  );
}

function flattenCapabilities(
  caps: Record<string, unknown>,
  prefix = '',
): Array<{ label: string; enabled: boolean }> {
  const badges: Array<{ label: string; enabled: boolean }> = [];
  for (const [key, val] of Object.entries(caps)) {
    if (val != null && typeof val === 'object' && !Array.isArray(val)) {
      badges.push(
        ...flattenCapabilities(val as Record<string, unknown>, prefix ? `${prefix}.${key}` : key),
      );
    } else {
      const label = prefix ? `${prefix}.${key}` : key;
      badges.push({ label, enabled: !!val });
    }
  }
  return badges;
}
